import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UserPlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const Field = ({ label, children }) => (
  <div>
    <Label className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);

export default function NewShipment() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    sender_name: "", sender_phone: "", sender_address: "",
    recipient_name: "", recipient_phone: "", recipient_address: "", recipient_email: "",
    weight_kg: "", dimensions: "", content: "", declared_value: "",
    shipping_type: "aerien", destination_city: "Abidjan",
    shipped_date: "", estimated_delivery: "", note: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, []);

  const fillFrom = (role) => (clientId) => {
    const c = clients.find((x) => x.id === clientId);
    if (!c) return;
    setForm((f) => ({
      ...f,
      [`${role}_name`]: c.full_name,
      [`${role}_phone`]: c.phone,
      [`${role}_address`]: c.address,
      ...(role === "recipient"
        ? { recipient_email: c.email || f.recipient_email, destination_city: c.city || f.destination_city }
        : {}),
    }));
    toast.success(`Informations de ${c.full_name} chargées`);
  };

  const ClientPicker = ({ role, testId }) => (
    <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50/60 p-4">
      <Label className="font-mono text-[10px] uppercase tracking-wider text-[#1E3A8A]">
        Sélectionner un client existant
      </Label>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <Select onValueChange={fillFrom(role)}>
          <SelectTrigger className="h-11 bg-white sm:flex-1" data-testid={testId}>
            <SelectValue placeholder={clients.length ? "Choisir un client…" : "Aucun client enregistré"} />
          </SelectTrigger>
          <SelectContent>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.full_name} · {c.phone}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" className="h-11 gap-2 border-slate-300 bg-white"
                onClick={() => navigate("/admin/clients")} data-testid={`${testId}-manage`}>
          <UserPlus className="h-4 w-4" /> Gérer les clients
        </Button>
      </div>
    </div>
  );

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post("/shipments", {
        sender: { name: form.sender_name, phone: form.sender_phone, address: form.sender_address },
        recipient: { name: form.recipient_name, phone: form.recipient_phone, address: form.recipient_address },
        recipient_email: form.recipient_email || null,
        weight_kg: parseFloat(form.weight_kg),
        dimensions: form.dimensions || null,
        content: form.content,
        declared_value: form.declared_value ? parseFloat(form.declared_value) : null,
        shipping_type: form.shipping_type,
        destination_city: form.destination_city,
        shipped_date: form.shipped_date || null,
        estimated_delivery: form.estimated_delivery || null,
        note: form.note || null,
      });
      toast.success(`Livraison créée : ${data.tracking_number}`);
      if (data.email_sent) toast.success("Email de suivi envoyé au destinataire");
      navigate(`/admin/livraison/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-8 sm:px-8">
      <div className="mx-auto max-w-4xl">
        <Button variant="ghost" className="mb-4 gap-2 pl-0 text-sm text-slate-600"
                onClick={() => navigate("/admin")} data-testid="back-dashboard-button">
          <ArrowLeft className="h-4 w-4" /> Retour au tableau de bord
        </Button>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
          Nouvelle livraison
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Le numéro de suivi (HTL-2026-XXXXX) est généré automatiquement.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-6" data-testid="new-shipment-form">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Expéditeur (Canada)</h2>
            <div className="mt-4">
              <ClientPicker role="sender" testId="sender-client-select" />
            </div>
            <div className="mt-2 grid gap-4 sm:grid-cols-2">
              <Field label="Nom complet">
                <Input required value={form.sender_name} onChange={set("sender_name")} className="h-11"
                       data-testid="sender-name-input" />
              </Field>
              <Field label="Téléphone">
                <Input required value={form.sender_phone} onChange={set("sender_phone")} className="h-11"
                       data-testid="sender-phone-input" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Adresse au Canada">
                  <Input required value={form.sender_address} onChange={set("sender_address")} className="h-11"
                         data-testid="sender-address-input" />
                </Field>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Destinataire (Côte d'Ivoire)</h2>
            <div className="mt-4">
              <ClientPicker role="recipient" testId="recipient-client-select" />
            </div>
            <div className="mt-2 grid gap-4 sm:grid-cols-2">
              <Field label="Nom complet">
                <Input required value={form.recipient_name} onChange={set("recipient_name")} className="h-11"
                       data-testid="recipient-name-input" />
              </Field>
              <Field label="Téléphone">
                <Input required value={form.recipient_phone} onChange={set("recipient_phone")} className="h-11"
                       data-testid="recipient-phone-input" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Adresse en Côte d'Ivoire">
                  <Input required value={form.recipient_address} onChange={set("recipient_address")}
                         className="h-11" data-testid="recipient-address-input" />
                </Field>
              </div>
              <Field label="Ville de destination">
                <Input required value={form.destination_city} onChange={set("destination_city")} className="h-11"
                       data-testid="destination-city-input" />
              </Field>
              <Field label="Email (pour l'envoi automatique du suivi)">
                <Input type="email" value={form.recipient_email} onChange={set("recipient_email")}
                       placeholder="client@exemple.com" className="h-11"
                       data-testid="recipient-email-input" />
              </Field>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Détails du colis</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Poids (kg)">
                <Input required type="number" step="0.1" min="0" value={form.weight_kg}
                       onChange={set("weight_kg")} className="h-11" data-testid="weight-input" />
              </Field>
              <Field label="Dimensions (cm)">
                <Input value={form.dimensions} onChange={set("dimensions")} placeholder="60x40x40"
                       className="h-11" data-testid="dimensions-input" />
              </Field>
              <Field label="Valeur déclarée (CAD)">
                <Input type="number" step="0.01" min="0" value={form.declared_value}
                       onChange={set("declared_value")} className="h-11" data-testid="declared-value-input" />
              </Field>
              <Field label="Type d'envoi">
                <Select value={form.shipping_type}
                        onValueChange={(v) => setForm((f) => ({ ...f, shipping_type: v }))}>
                  <SelectTrigger className="h-11" data-testid="shipping-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aerien">Aérien</SelectItem>
                    <SelectItem value="maritime">Maritime</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Date d'expédition">
                <Input type="date" value={form.shipped_date} onChange={set("shipped_date")} className="h-11"
                       data-testid="shipped-date-input" />
              </Field>
              <Field label="Livraison estimée">
                <Input type="date" value={form.estimated_delivery} onChange={set("estimated_delivery")}
                       className="h-11" data-testid="estimated-delivery-input" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Contenu déclaré">
                  <Textarea required value={form.content} onChange={set("content")} rows={3}
                            data-testid="content-input" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Note interne (optionnel)">
                  <Textarea value={form.note} onChange={set("note")} rows={2} data-testid="note-input" />
                </Field>
              </div>
            </div>
          </section>

          {error && (
            <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-[#DC2626]"
               data-testid="new-shipment-error">
              {error}
            </p>
          )}

          <Button type="submit" disabled={saving}
                  className="h-12 w-full bg-[#1E3A8A] text-base font-bold text-white hover:bg-[#172d6d] sm:w-auto sm:px-10"
                  data-testid="submit-shipment-button">
            {saving ? "Création…" : "Créer la livraison"}
          </Button>
        </form>
      </div>
    </div>
  );
}
